import defu from 'defu';

export async function usePbApi<T>(url: string, options: any = {}) {
    const auth = useAuthStore();
    const config = useRuntimeConfig();

    const defaults = {
        baseURL: config.public.pb.apiDomain,
        headers: {
            Accept: 'application/json',
        },
    };

    const params = defu(options, defaults);

    let status: number = 200;
    let message: string = '';
    let data: T | null = null;
    let retryAttempted = false;

    async function makeRequest(): Promise<void> {
        // Get a valid token (handles refresh and queueing automatically)
        const token = await auth.getValidTokenForRequest();
        
        // Set Authorization header if we have a token
        if (token) {
            params.headers.Authorization = token;
        }

        const result = await $fetch<T>(url, {
            ...params,
            retry: 0,
            onResponse({ response }) {
                status = response.status;
            },
        });
        data = result as T;
    }

    try {
        await makeRequest();
    } catch (err: any) {
        status = err.response?.status || 500;

        if (status === 401 && !retryAttempted) {
            // Token might be expired, try refreshing and retry once
            retryAttempted = true;
            
            try {
                const refreshed = await auth.refreshAccessToken();
                if (refreshed) {
                    await makeRequest();
                } else {
                    message = 'Authentication failed';
                }
            } catch (retryErr: any) {
                // If retry also fails, use the retry error
                status = retryErr.response?.status || 500;
                if (retryErr.response?._data?.errors && Array.isArray(retryErr.response._data.errors)) {
                    message = retryErr.response?._data?.errors[0]?.message || 'An error occurred';
                } else if (retryErr.response?._data?.message) {
                    message = retryErr.response._data.message;
                } else {
                    message = retryErr.message || retryErr.statusText || 'An error occurred';
                }
            }
        } else {
            // Return the full error structure for non-401 errors or already retried
            if (err.response?._data?.errors && Array.isArray(err.response._data.errors)) {
                message = err.response?._data?.errors[0]?.message || 'An error occurred';
            } else if (err.response?._data?.message) {
                // Fallback to direct message property
                message = err.response._data.message;
            } else {
                message = err.message || err.statusText || 'An error occurred';
            }
        }
    }

    return { data: data as T, status, message };
}
